import {
  Close,
  Person,
  Phone,
  Business,
  CalendarToday,
  Event,
  Description,
  CheckCircle,
  Info,
  Assignment,
  Category,
  Flag,
  Work,
} from "@mui/icons-material";
import {
  Stack,
  Button,
  Box,
  Typography,
  useTheme,
  Grid,
  alpha,
} from "@mui/material";
import React from "react";
import { RightPanel } from "../../../components/RightPanel";
import { formatDateTimeWithLocale } from "../../../helpers";
import { IActionItem } from "../../../redux/actionItems";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";

interface IViewChatLeadDialogProps {
  open: boolean;
  actionItem: IActionItem | null;
  onClose: () => void;
}

const getFieldIcon = (key: string): React.ElementType => {
  const iconMap: Record<string, React.ElementType> = {
    status: CheckCircle,
    action_item_uuid: Assignment,
    name: Person,
    mobile_no: Phone,
    item: Assignment,
    category_name: Category,
    priority: Flag,
    description: Description,
    assigned_to_name: Work,
    conversation_id: Info,
    created_by_name: Person,
    modified_by_name: Person,
    create_ts: CalendarToday,
    insert_ts: CalendarToday,
  };

  return iconMap[key.toLowerCase()] || Info;
};

const FIELD_LABEL_OVERRIDES: Record<string, string> = {
  create_ts: "Created On",
  insert_ts: "Last Updated On",
  action_item_uuid: "Action Item ID",
  mobile_no: "Mobile Number",
  category_name: "Category",
  assigned_to_name: "Assigned To",
  //   conversation_id: "Conversation ID",
};

const formatFieldName = (key: string): string => {
  if (FIELD_LABEL_OVERRIDES[key]) {
    return FIELD_LABEL_OVERRIDES[key];
  }
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatFieldValue = (key: string, value: any, ftDate: any): string => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  const lowerKey = key.toLowerCase();
  if (lowerKey.includes("date") || lowerKey.includes("_ts")) {
    if (lowerKey.includes("_ts")) {
      return formatDateTimeWithLocale(String(value));
    } else {
      return ftDate(String(value)) || String(value);
    }
  }

  return String(value);
};

const shouldExcludeField = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return (
    lowerKey.includes("_uuid") ||
    lowerKey.endsWith("uuid") ||
    lowerKey === "action_item_unique_id" ||
    lowerKey === "category_code" ||
    lowerKey === "assigned_to_uuid" ||
    lowerKey === "created_by_uuid" ||
    lowerKey === "modified_by_uuid" ||
    lowerKey === "company_outlet_uuid"
  );
};

const SectionHeader: React.FC<{ title: string }> = ({ title }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="subtitle2"
      sx={{
        fontWeight: 600,
        color: theme.palette.primary.main,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontSize: "0.7rem",
        mb: 1,
      }}
    >
      {title}
    </Typography>
  );
};

const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number | null | undefined;
}> = ({ icon, label, value }) => {
  const theme = useTheme();

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
      <Box sx={{ color: theme.palette.primary.main, display: "flex" }}>
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: theme.palette.text.secondary, minWidth: 90 }}
      >
        {label}:
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: value
            ? theme.palette.text.primary
            : theme.palette.text.disabled,
        }}
      >
        {value || "—"}
      </Typography>
    </Stack>
  );
};

const CHAT_LEAD_DETAILS_SECTIONS = [
  {
    title: "Customer Information",
    fields: ["name", "mobile_no"],
  },
  {
    title: "Action Item Information",
    fields: ["item", "category_name", "priority", "status"],
  },
  {
    title: "Assignment Information",
    fields: ["assigned_to_name", "description"],
  },
  {
    title: "Additional Information",
    fields: ["created_by_name", "modified_by_name", "create_ts", "insert_ts"],
  },
];

export const ViewChatLeadDialog: React.FC<IViewChatLeadDialogProps> = ({
  open,
  actionItem,
  onClose,
}) => {
  const theme = useTheme();
  const { ftDate } = useTimezoneFormatter();

  if (!actionItem) {
    return null;
  }

  return (
    <RightPanel
      heading="View Chat Lead Details"
      open={open}
      width="80%"
      onClose={onClose}
      actionButtons={() => (
        <Box display={"flex"} justifyContent={"space-between"} width="100%">
          <Stack direction={"row"} spacing={2} justifyContent={"flex-start"}>
            <Button variant="contained" onClick={onClose}>
              <Close sx={{ fontSize: 18, mr: 0.5 }} />
              Close
            </Button>
          </Stack>
        </Box>
      )}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
        <Grid container spacing={2} alignItems="stretch">
          {CHAT_LEAD_DETAILS_SECTIONS.map((section) => {
            const visibleFields = section.fields.filter(
              (key) =>
                actionItem?.[key as keyof IActionItem] !== undefined &&
                !shouldExcludeField(key),
            );
            if (visibleFields.length === 0) return null;

            return (
              <Grid item xs={12} md={4} key={section.title}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: alpha(theme.palette.grey[500], 0.04),
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <SectionHeader title={section.title} />
                  <Box sx={{ flex: 1 }}>
                    {visibleFields.map((key) => {
                      const Icon = getFieldIcon(key);
                      const formattedLabel = formatFieldName(key);
                      const fieldValue = actionItem[key as keyof IActionItem];
                      const formattedValue = formatFieldValue(
                        key,
                        fieldValue,
                        ftDate,
                      );

                      return (
                        <InfoItem
                          key={key}
                          icon={<Icon sx={{ fontSize: 16 }} />}
                          label={formattedLabel}
                          value={formattedValue}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </RightPanel>
  );
};
